import { WellyMark } from "@/components/WellyMark";
import type { ProgressStepView } from "@/lib/progress-stream";

/**
 * The live feed of what a search is doing right now.
 *
 * ---------------------------------------------------------------------------
 * Every line on this screen is a completed `await`
 * ---------------------------------------------------------------------------
 * There is no timer here, no estimate, and no progress bar. Each entry arrives
 * from the server after the work it names has already finished — the phase
 * vocabulary and the emission points are in `src/lib/shopping/progress.ts`. A
 * bar that advanced on a clock would be worse than the spinner it replaced: it
 * would be a confident claim about a state nobody checked, which is exactly the
 * defect class this product exists to be the opposite of.
 *
 * That constraint is also why an unrecognised phase renders as *nothing*
 * (see {@link describeStep}). A newer server saying something this build does
 * not have wording for is a reason to stay quiet, not to invent a sentence.
 *
 * ---------------------------------------------------------------------------
 * The completeness claim survives into the feed
 * ---------------------------------------------------------------------------
 * `grounding-title-only` and `category-ready` carry the grounding mode, and the
 * copy below says plainly when a search is a keyword match rather than a worked
 * department. Narrating three department probes and then quietly searching by
 * product name would make the false completeness claim `groundingMode` exists
 * to prevent (#74).
 *
 * That is why no line here ever says "every", "all", or "the whole category" —
 * and why `completeness-claims.test.tsx` asserts it mechanically over every
 * phase rather than trusting review to catch it. The overclaim is one adjective
 * away at all times and `BANNED_SUBSTRINGS` does not cover it.
 *
 * ---------------------------------------------------------------------------
 * The three numbers, and the ones that do not exist (#147)
 * ---------------------------------------------------------------------------
 * The feed shows exactly the funnel the pipeline observes: Keepa's own
 * post-filter `totalResults`, how many histories we actually pulled, and how
 * the screen sorted them into `shortlisted` / `set-aside` / `filtered-out` /
 * `unverified`. It does **not** show a "340 -> 61 -> 22 -> 12" tier funnel:
 * price, rating, review-count and offer-count filters are applied server-side
 * by Keepa, so those intermediate counts are not observed and rendering them
 * would be fabrication wearing a fact's clothes.
 *
 * Every number below comes from `step.counts`, which the emitters fill with the
 * same integers the finished `SearchResult` carries.
 *
 * ---------------------------------------------------------------------------
 * Listing sentences are not written here
 * ---------------------------------------------------------------------------
 * A `finding` step arrives with `evidence` already composed by
 * `describeSignalEvidence` (`src/lib/shopping/present.ts`) — the single
 * implementation of the hedged, evidence-backed phrasing CLAUDE.md requires.
 * This component prints those strings and adds none of its own: describe the
 * signal, never the actor.
 *
 * ---------------------------------------------------------------------------
 * "Does the shopper have to stay?" — deliberately unanswered here (#147)
 * ---------------------------------------------------------------------------
 * The obvious follow-up to a multi-minute wait is "let them leave and email
 * them", and this component does not attempt it. Leaving and returning is not
 * technically possible yet: the run lives entirely inside one request, so
 * closing the tab ends it — surviving a disconnect is #155's durable-execution
 * work, and the transport note in `functions/lib/progress.ts` says the same.
 * Whether the product *should* encourage leaving is a separate call that
 * interacts with the fee moment (#146) and belongs to the operator, not to this
 * PR. Recorded as deferred rather than half-built: a notification affordance
 * that quietly did nothing would be worse than the wait.
 */

/** How many entries stay on screen. A long build would otherwise run off it. */
const VISIBLE_STEPS = 8;

export interface SearchProgressProps {
  steps: ProgressStepView[];
  /** What this run is for, so the heading is true of it. */
  heading: string;
}

export function SearchProgress({ steps, heading }: SearchProgressProps) {
  const described = steps
    .map((step, index) => ({ index, step, line: describeStep(step) }))
    .filter((entry) => entry.line !== null)
    .slice(-VISIBLE_STEPS);

  const latest = described.length > 0 ? described[described.length - 1].index : -1;

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[60vh] flex-col items-center justify-center gap-4"
    >
      <WellyMark size={56} className="animate-pulse text-primary" />
      <p className="text-sm text-muted-foreground">{heading}</p>

      {described.length === 0 ? (
        // Honest about its own emptiness: the first step appears when the first
        // piece of work finishes, which on a cold build is a real wait.
        <p className="max-w-md text-center text-xs text-muted-foreground">
          Nothing to report yet — each line below appears once that piece of work has actually
          finished.
        </p>
      ) : (
        <ul className="w-full max-w-md space-y-1">
          {described.map((entry) => (
            <li
              key={entry.index}
              className={
                entry.step.failed
                  ? "text-sm text-destructive"
                  : entry.index === latest
                    ? "text-sm text-foreground"
                    : "text-sm text-muted-foreground"
              }
            >
              {entry.line}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * One literal sentence per phase, or `null` for a phase this build does not
 * know.
 *
 * Deliberately flat and factual. Each string restates what the completed
 * `await` returned and nothing more — no counts we did not observe (Keepa
 * applies its filters server-side, so the intermediate funnel does not exist to
 * report), no verdicts, no timing advice.
 *
 * A failed step gets a phase-specific line where the generic one would be
 * *wrong* rather than merely terse — a token bucket that will never admit this
 * call, a build that crashed, a relaxation loop with nothing left to loosen.
 * Everything else falls through to the shared suffix, which is enough: naming
 * the step is what the shopper needs.
 */
export function describeStep(step: ProgressStepView): string | null {
  if (step.failed === true) {
    const named = describeFailure(step);
    if (named !== null) return named;
  }
  const line = describePhase(step);
  if (line === null) return null;
  return step.failed === true ? `${line} — that step failed` : line;
}

/**
 * Failures whose honest wording is not "the same thing, but it failed".
 *
 * The `category-build` entry is the load-bearing one. #144 exists because a
 * crashed build reported to the shopper as "we don't cover that yet"; the feed
 * would reintroduce that exact conflation if a crash and a coverage decision
 * read the same in flight, so this line says whose fault it is.
 */
function describeFailure(step: ProgressStepView): string | null {
  switch (step.phase) {
    case "category-build":
      return "The category build broke on our side — that's ours to fix, not something we've decided not to cover";
    case "category-unconfigured":
      return "This deployment has no Amazon data credentials, so there was nothing to search — ours to fix, not a gap in what we cover";
    case "keepa-wait":
      return step.detail === "exceeds-capacity"
        ? // Non-retriable by construction: the bucket's ceiling is below what
          // this call needs, so no amount of waiting changes the answer. Saying
          // "still working" here would be the lie the whole feed exists against.
          "This search needs more of the shared Amazon data budget than it can hold at once — waiting wouldn't help, so we stopped rather than leaving you here"
        : `We waited ${formatSeconds(step.counts?.waitedMs)} for our turn at the shared Amazon data budget and it didn't come free`;
    case "grounding-verified":
      // Not a rejection. The check itself broke, so we have no verdict — and
      // "that department didn't match" would be a verdict we never got.
      return `We couldn't get a verdict on ${step.detail ?? "a department"} — the check itself broke`;
    case "relaxed":
      return "Nothing matched, and there's nothing left to loosen — we'd rather say so than pad the list";
    case "answer-read":
      return step.detail === undefined
        ? "We still need an answer before this can run"
        : `We still need an answer for ${step.detail}`;
    default:
      return null;
  }
}

/**
 * The screening result — DESIGN.md §6's headline, live.
 *
 * "The 4 travel cribs worth buying, and why the other 336 aren't" rendered as
 * it happens, in the four categories the code actually produces. A bucket with
 * nothing in it is omitted rather than printed as a zero: "0 set aside" is a
 * true statement that reads like a finding.
 */
function describeScreened(
  counts: Record<string, number> | undefined,
  detail: string | undefined,
): string {
  if (counts === undefined) {
    return `Ran the listing-integrity checks — ${detail ?? "on the shortlist"}`;
  }
  const parts = [
    part(counts.shortlisted, "came through clean"),
    part(counts["set-aside"], "set aside on a listing-integrity signal"),
    part(counts["filtered-out"], "no longer matching what you asked for"),
    part(counts.unverified, "without enough tracked history to check"),
  ].filter((entry): entry is string => entry !== null);

  const screened = counts.screened ?? 0;
  const headline = `Ran the listing-integrity checks on ${screened} ${screened === 1 ? "listing" : "listings"}`;
  return parts.length === 0 ? headline : `${headline} — ${parts.join(", ")}`;
}

function part(count: number | undefined, wording: string): string | null {
  return count === undefined || count === 0 ? null : `${count} ${wording}`;
}

/** Whole seconds, from a millisecond count the server observed. */
function formatSeconds(ms: number | undefined): string {
  if (ms === undefined) return "a while";
  return `${Math.max(1, Math.round(ms / 1000))}s`;
}

function describePhase(step: ProgressStepView): string | null {
  const detail = step.detail;
  const counts = step.counts;

  switch (step.phase) {
    case "category-known":
      return detail === undefined
        ? "This is a category we already cover"
        : `Already covered: ${detail}`;
    case "category-cached":
      return "Reusing the category we built for this need before";
    case "category-build":
      return "Nobody has asked for this before — building the category now";
    case "category-uncovered":
      return "There isn't enough here to build a category from — that's a gap in what we cover, and we'd rather say so than guess";
    case "category-unconfigured":
      return "This deployment has no Amazon data credentials, so there was nothing to search";

    case "grounding-terms":
      return detail === undefined
        ? "No Amazon department names came back for this wording"
        : `Departments worth checking: ${detail}`;
    case "grounding-shelves":
      return `Searched Amazon's departments — ${detail ?? "no term given"}`;
    case "grounding-probe":
      return `Read real products back from ${detail ?? "a department"}`;
    case "grounding-verified":
      return `Those products match what you asked for — using ${detail ?? "that department"}`;
    case "grounding-rejected":
      return `Those products are not what you asked for — skipping ${detail ?? "that department"}`;
    case "grounding-title-only":
      // The downgrade, said plainly and while it happens.
      return "No department checked out, so this searches by product name across the catalog";

    case "drafting-plan":
      return `Worked out which questions matter — ${detail ?? "planning done"}`;
    case "drafting-slot":
      return `Wrote a question: ${detail ?? "one more"}`;
    case "category-ready":
      return detail === "title-only"
        ? // States what this run *is*, rather than what it is not. The negated
          // form ("not a whole department") is honest but unguardable: a
          // substring check for a completeness claim cannot read the "not".
          "Category ready — this searches by product name across the catalog"
        : "Category ready, pinned to a verified Amazon department";

    case "answer-read":
      return `Read your answer for ${detail ?? "a question"}`;
    case "keepa-wait":
      // A queue position, not an estimate of the search. `waitMs` is the number
      // the shared token bucket itself returned when it turned this call away.
      return counts?.waitMs === undefined
        ? "Queued behind our shared Amazon data budget — waiting for our turn"
        : `Queued behind our shared Amazon data budget — it says ${formatSeconds(counts.waitMs)} until our turn`;
    case "finder":
      return `Amazon's catalog answered — ${detail ?? "results returned"}`;
    case "relaxed":
      return `Loosened a filter to find anything at all: ${detail ?? "one filter"}`;
    case "narrowed":
      return `Applied a default to cut this down: ${detail ?? "one filter"}`;
    case "histories":
      // Named for what it is — one batched fetch, not a listing-by-listing
      // crawl. The screen that follows is synchronous, so a ticker counting up
      // through listings would be pacing nobody measured.
      return `Pulled price, rating and review history in one go — ${detail ?? "for the shortlist"}`;
    case "screened":
      return describeScreened(counts, detail);
    case "finding":
      // The evidence sentences arrive already written, by `present.ts`. This
      // line puts a name in front of them and changes nothing else.
      return step.evidence === undefined || step.evidence.length === 0
        ? `Set aside ${detail ?? "a listing"} on a listing-integrity signal`
        : `Set aside ${detail ?? "a listing"} — ${step.evidence.join("; ")}`;

    default:
      // Unknown to this build. Saying nothing beats saying something invented.
      return null;
  }
}
